import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { PlatformSyncError } from 'src/pages/revenue/booking-settings/booking-settings.model';
import { environment } from 'src/environments/environment';
import { HTTP_TIMEOUT } from 'src/app/interceptors/timeout.interceptor';

/**
 * Writes here reconcile to Airbnb, VRBO and Booking.com inside the request, so
 * they routinely outrun the default 20s ceiling. Abandoning one doesn't stop
 * the server: the write often commits anyway, and the customer sees a failure
 * for a change that actually landed — then retries it. Waiting is the lesser
 * evil until the channel push moves out of the request cycle (backend 18380).
 */
const SETTINGS_WRITE_TIMEOUT = 120000;

/** Fresh context per call — HttpContext is mutable and must not be shared. */
function slowWrite(): HttpContext {
  return new HttpContext().set(HTTP_TIMEOUT, SETTINGS_WRITE_TIMEOUT);
}

/**
 * LEGACY. Original domain tag for the listing-settings blob. The Optimizer page
 * no longer writes here — every setting now rides the `optimizer` category as its
 * own row (see OPTIMIZER_SETTING_CATEGORY). Kept only so historical rows can still
 * be identified if a migration ever needs it.
 */
export const LISTING_SETTING_CATEGORY = 'listing';

/** LEGACY. Key of the removed single-blob row that used to hold the whole Optimizer settings object. */
export const LISTING_OPTIMIZATION_BLOB_KEY = 'listing_optimization_settings';

/**
 * Domain tag for the whole Optimizer page inside the shared `address_settings`
 * key/value store (gotidy #8211 made the store category-aware so rows never leak
 * into the guest guidebook). Every Optimizer setting — the account-default price
 * bounds (base/min/max_price, validated + PriceLabs-synced per gotidy #8271) AND
 * the rest of the page (one row per setting) — lives under this category. The
 * `category` is mandatory on write — sandbox3 400s without it.
 */
export const OPTIMIZER_SETTING_CATEGORY = 'optimizer';

/**
 * Account-default price bounds, one optimizer-category row per field. All
 * values in cents (whole dollars — multiples of 100); null = not stored.
 */
export interface OptimizerPricingDefaults {
  basePrice: number | null;
  minPrice: number | null;
  maxPrice: number | null;
}

/**
 * A partial write of the pricing defaults. Per field: a cents value upserts the
 * row; `null` or `undefined` leaves it as stored.
 *
 * There is deliberately no "clear" here. `data: null` no longer soft-deletes
 * (the backend requires `data` on every entry), and the DELETE endpoint refuses
 * base/min/max_price precisely because they're platform-synced — so a stored
 * price bound can only be replaced, never removed. Blanking the field in the UI
 * therefore means "leave as stored", matching how blank min/max stay behaves.
 */
export interface OptimizerPricingDefaultsWrite {
  basePrice?: number | null;
  minPrice?: number | null;
  maxPrice?: number | null;
}

const OPTIMIZER_KEY_BY_FIELD: ReadonlyArray<[keyof OptimizerPricingDefaultsWrite, string]> = [
  ['basePrice', 'base_price'],
  ['minPrice', 'min_price'],
  ['maxPrice', 'max_price'],
];

/**
 * A single row returned by `GET .../addresses/:id?includes=address_settings`.
 * Unlike AddressGuidebookService's `AddressSetting`, this includes `category`
 * (PR #8211 added it to the response entity) so callers can filter by domain.
 */
export interface AddressSetting {
  id: number;
  key: string;
  category: string;
  data: Record<string, unknown> | null;
  /**
   * Whether every connected, supported platform has confirmed the value:
   * `true` confirmed everywhere, `false` while a write is pending or failed,
   * `null` for settings that sync nowhere. Saving stages reconciliation
   * asynchronously, so the response to a PUT normally still reads `false` —
   * and a `false` is never final, since failed writes retry after 1, 5 and 15
   * minutes with a daily sweep behind that.
   */
  synced?: boolean | null;
  /**
   * Per-platform confirmation, containing ONLY platforms both connected to the
   * address and supported by that setting — so `booking_com` appears for
   * `min_stay` but never for `max_stay` or `max_booking_advance_months`, and an
   * unconnected platform simply doesn't appear. `{}` outside the optimizer
   * category and for optimizer keys that sync nowhere.
   */
  platform_sync?: Partial<Record<OptimizerPlatform, PlatformSyncStatus>>;
  /**
   * The last push error of each failed platform: a readable sentence, plus the parts the
   * platform would not take (single photos) when there are any.
   */
  platform_sync_errors?: Partial<Record<OptimizerPlatform, PlatformSyncError>>;
  created_at: string;
  updated_at: string;
}

/** Platform slugs used by `platform_sync`. */
export type OptimizerPlatform = 'airbnb' | 'vrbo' | 'booking_com';

/**
 * How one platform is doing with a setting. Was a boolean, which could not tell the two
 * unsynced states apart: saving a value resets the row to unsynced, so a push still in
 * flight and a push the platform rejected both read as `false`.
 *
 * `pending` and `failed` both keep retrying (1, 5 and 15 minutes, then a daily sweep), so
 * neither is a final answer. The calendar's per-date rows use the same three plus a
 * `configured` that only means anything per date — see PlatformDiscountStatus.
 *
 * `not_supported` marks a connected channel the setting can never be pushed to (e.g. a
 * security deposit on Airbnb) — final, nothing is retried.
 */
export type PlatformSyncStatus = 'synced' | 'pending' | 'failed' | 'not_supported';

/**
 * What a per-field sync dot shows, aggregated over a field's platforms. The three
 * backend states plus `partial`: at least one platform synced while another failed —
 * a partial success worth its own colour, not a flat red.
 */
export type SyncDotStatus = PlatformSyncStatus | 'partial';

export interface UpsertListingEntry {
  key: string;
  /** Required by the backend — an entry without `data` is rejected. Deleting goes through `deleteSettings`. */
  data: Record<string, unknown>;
  /**
   * Overrides the caller's default category. The endpoint takes one category PER setting, so a
   * single save can carry rows from more than one — the Listings page uses it to write
   * `pets_allowed` (optimizer) alongside its `listing` content.
   */
  category?: string;
}

export interface AddressWithListingSettings {
  id: number;
  address_settings?: AddressSetting[];
  [key: string]: unknown;
}

/**
 * Thin transport for the "listing" domain of `address_settings`. Mirrors
 * AddressGuidebookService (GET address with settings, PUT upsert) but tags every
 * write with `category: 'listing'` and filters reads to that category. Keeps no
 * knowledge of the page's settings shape — serialization lives in
 * `booking-settings.model.ts`.
 */
@Injectable({ providedIn: 'root' })
export class ListingOptimizationSettingsService {

  private readonly http = inject(HttpClient);

  async getAddressWithSettings(
    addressId: number,
    extraIncludes: string[] = []
  ): Promise<AddressWithListingSettings> {
    const includes = this.buildIncludes(extraIncludes);
    const url = `${environment.api_url}api/v1/customer/addresses/${addressId}?includes=${includes}`;
    return firstValueFrom(this.http.get<AddressWithListingSettings>(url));
  }

  async upsertListingSettings(
    addressId: number,
    settings: UpsertListingEntry[],
    extraIncludes: string[] = []
  ): Promise<AddressWithListingSettings> {
    const includes = this.buildIncludes(extraIncludes);
    const url = `${environment.api_url}api/v1/customer/addresses/${addressId}/address-settings?includes=${includes}`;
    // Every Optimizer-page setting rides the `optimizer` category (one row per
    // setting — no blob). The backend requires a top-level `category` per setting.
    const body = settings.map(setting => ({ category: OPTIMIZER_SETTING_CATEGORY, ...setting }));
    return firstValueFrom(
      this.http.put<AddressWithListingSettings>(url, { settings: body }, { context: slowWrite() })
    );
  }

  /**
   * Upserts the Listing Details content (title/description/security_deposit/bedrooms/
   * bathrooms/square_feet/amenities) under the `listing` category — same endpoint and
   * UpsertMany use case as the optimizer settings, different category tag.
   */
  async upsertListingContent(addressId: number, entries: UpsertListingEntry[]): Promise<void> {
    if (!entries.length) { return; }
    const url = `${environment.api_url}api/v1/customer/addresses/${addressId}/address-settings`;
    const body = entries.map(entry => ({ category: LISTING_SETTING_CATEGORY, ...entry }));
    await firstValueFrom(this.http.put(url, { settings: body }, { context: slowWrite() }));
  }

  /** The address's security deposit (listing-category row), in cents; null when not set. */
  async getListingSecurityDeposit(addressId: number): Promise<number | null> {
    const url = `${environment.api_url}api/v1/customer/addresses/${addressId}/address-settings?category=${LISTING_SETTING_CATEGORY}`;
    const rows = await firstValueFrom(this.http.get<AddressSetting[]>(url));
    const value = (rows ?? []).find(r => r.key === 'security_deposit')?.data?.['value'];
    return typeof value === 'number' ? value : null;
  }

  /** The listing's title and description (listing-category rows); '' for either when not set. */
  async getListingSummary(addressId: number): Promise<{ title: string; description: string }> {
    const url = `${environment.api_url}api/v1/customer/addresses/${addressId}/address-settings?category=${LISTING_SETTING_CATEGORY}`;
    const rows = await firstValueFrom(this.http.get<AddressSetting[]>(url));
    const text = (key: string): string => {
      const value = (rows ?? []).find(r => r.key === key)?.data?.['value'];
      return typeof value === 'string' ? value : '';
    };
    return { title: text('title'), description: text('description') };
  }

  /**
   * Reads the account-default price bounds (optimizer-category rows). A field
   * with no stored default is null. Values are cents.
   */
  async getOptimizerDefaults(addressId: number): Promise<OptimizerPricingDefaults> {
    const url = `${environment.api_url}api/v1/customer/addresses/${addressId}/address-settings?category=${OPTIMIZER_SETTING_CATEGORY}`;
    const rows = await firstValueFrom(this.http.get<AddressSetting[]>(url));
    const valueFor = (key: string): number | null => {
      const row = (rows ?? []).find(r => r.key === key);
      const value = row?.data?.['value'];
      return typeof value === 'number' ? value : null;
    };
    return {
      basePrice: valueFor('base_price'),
      minPrice: valueFor('min_price'),
      maxPrice: valueFor('max_price'),
    };
  }

  /**
   * Upserts the account-default price bounds. Sends only the fields that carry
   * a value — a null/absent field is skipped rather than cleared (see
   * OptimizerPricingDefaultsWrite). The backend validates (multiples of 100 cents,
   * min ≤ max against stored values) and pushes the result to PriceLabs — a
   * 400 means NOTHING saved.
   */
  async upsertOptimizerDefaults(addressId: number, write: OptimizerPricingDefaultsWrite): Promise<void> {
    const settings = OPTIMIZER_KEY_BY_FIELD
      .filter(([field]) => write[field] !== undefined && write[field] !== null)
      .map(([field, key]) => ({
        category: OPTIMIZER_SETTING_CATEGORY,
        key,
        data: { value: write[field] },
      }));
    if (!settings.length) { return; }
    const url = `${environment.api_url}api/v1/customer/addresses/${addressId}/address-settings`;
    await firstValueFrom(this.http.put(url, { settings }, { context: slowWrite() }));
  }

  /**
   * Removes settings outright. Both `category` and `keys` are required; the
   * response is 204.
   *
   * Under `optimizer` the platform-synced keys are refused with a 400 — the
   * seven `discount_*` levers, `min_stay`, `max_stay` and the three price
   * bounds. Those must be given an explicit value instead (a discount lever is
   * turned off with `enabled: false`), because a hard delete would leave the
   * old value sitting on the platform.
   */
  async deleteSettings(addressId: number, category: string, keys: string[]): Promise<void> {
    if (!keys.length) { return; }
    const params = new URLSearchParams({ category });
    for (const key of keys) { params.append('keys[]', key); }
    const url = `${environment.api_url}api/v1/customer/addresses/${addressId}/address-settings?${params.toString()}`;
    await firstValueFrom(this.http.delete(url, { context: slowWrite() }));
  }

  /** Filters the address's settings to `category === 'optimizer'` and maps `key -> data`. */
  listingSettingsMap(address: AddressWithListingSettings | undefined): Record<string, Record<string, unknown> | null> {
    const map: Record<string, Record<string, unknown> | null> = {};
    for (const setting of address?.address_settings ?? []) {
      if (setting.category === OPTIMIZER_SETTING_CATEGORY) {
        map[setting.key] = setting.data ?? null;
      }
    }
    return map;
  }

  private buildIncludes(extraIncludes: string[]): string {
    const merged = ['address_settings', ...extraIncludes];
    return Array.from(new Set(merged)).join(',');
  }
}
